import { ArrowUpFromLine, Bath, ChefHat, PaintRoller, Building2, Blocks } from "lucide-react";

export const COMPANY = {
  name: "Esskay Homes",
  legalName: "Esskay Homes Limited",
  reg: "11076237",
  address: "30a York Road, Aldershot, Hampshire, GU11 3JP",
  area: "Serving London & the South East",
  phone: "0203 488 1081",
  phoneHref: "tel:+442034881081",
  email: "info@esskayhomes.co.uk",
  hours: "Mon – Sat · 9am – 5pm",
  instagram: "https://www.instagram.com/esskayhomes/",
  facebook: "https://www.facebook.com/esskayhomes",
  tagline: "The Home Improvement Experts",
};

const u = (id, w = 1600) =>
  `https://images.unsplash.com/photo-${id}?q=80&w=${w}&auto=format&fit=crop`;

export const IMG = {
  hero: u("1538729737466-d4ea35e17af7", 2200),
  loft: u("1650346910668-8db744da8991"),
  bathrooms: u("1756079664354-34944e001f6d"),
  bathrooms2: u("1564540583246-934409427776"),
  bathrooms3: u("1564540579594-0930edb6de43"),
  kitchens: u("1663811396777-05505d999151"),
  kitchens2: u("1639405069836-f82aa6dcb900"),
  kitchens3: u("1502005097973-6a7082348e28"),
  refurb: u("1768321916027-d0f69289227f"),
  refurb2: u("1768321916886-30dc323bddf7"),
  newBuilds: u("1513584684374-8bab748fbf90"),
  newBuilds2: u("1732721176408-5444b1641550"),
  extensions: u("1572534382964-664044795b84"),
  extensions2: u("1598307754921-da47ead0ad5f"),
  craft: u("1659930087003-2d64e33181f7"),
  craft2: u("1482731215275-a1f151646268"),
  plans: u("1503387762-592deb58ef4e"),
  living1: u("1628744876497-eb30460be9f6"),
  living2: u("1603039531759-1a1bbe4f9f94"),
  living3: u("1648536474504-7bbf8a1ee3ab"),
  brick: u("1607900840367-a83dcd63296d"),
};

export const SERVICES = [
  {
    slug: "loft-conversions",
    name: "Loft Conversions",
    short: "Dormer, mansard & Velux conversions that unlock a whole new floor.",
    tagline: "Unlock the space already in your home.",
    icon: ArrowUpFromLine,
    image: IMG.loft,
    altImage: IMG.plans,
    description: [
      "Your loft is the quickest way to add a bedroom, office or studio without moving house. We handle everything in-house — structural steel design, the new staircase, insulation, windows and finishes — turning unused roof space into a beautifully finished room that feels like it was always meant to be there.",
      "From simple Velux conversions to full dormer and mansard builds, every project is surveyed, drawn and certified before a single tool is lifted, so there are no surprises along the way.",
    ],
    includes: [
      "Dormer, mansard, hip-to-gable & Velux conversions",
      "Structural calculations & building-control approval",
      "Custom staircase & first-fix joinery",
      "Insulation, plastering & flooring",
      "Plumbing, electrics & smart lighting",
      "Velux windows & Juliet balconies",
      "Full decoration & finishing",
      "12-month workmanship warranty",
    ],
  },
  {
    slug: "bathrooms",
    name: "Bathrooms",
    short: "Spa-grade bathrooms and wet rooms, fitted with millimetre precision.",
    tagline: "Spa-grade bathrooms, built around you.",
    icon: Bath,
    image: IMG.bathrooms,
    altImage: IMG.bathrooms2,
    description: [
      "A great bathroom is invisible engineering: perfectly falls-level tiling, whisper-quiet extraction, water pressure that never disappoints. We design and build bathrooms that feel like a private spa — underfloor heating, rainfall showers, backlit mirrors and storage that hides everything.",
      "Whether it's a compact en-suite or a full family wet room, we manage the whole fit-out — plumbing, electrics, tiling, joinery and decoration — from rip-out to handover, usually within two to three weeks.",
    ],
    includes: [
      "Full design consultation & 3D layout",
      "Wet rooms & walk-in showers",
      "Underfloor heating & heated rails",
      "Large-format & mosaic tiling",
      "Plumbing, drainage & ventilation",
      "LED & mirrored lighting design",
      "Bespoke vanity joinery",
      "Complete rip-out & disposal",
    ],
  },
  {
    slug: "kitchens",
    name: "Kitchens",
    short: "Bespoke kitchens engineered around how your family really cooks.",
    tagline: "The heart of your home, rebuilt.",
    icon: ChefHat,
    image: IMG.kitchens,
    altImage: IMG.kitchens2,
    description: [
      "The kitchen is where your home works hardest. We design and install bespoke kitchens that balance beauty with brutal practicality — handleless cabinetry, stone worktops, integrated appliances and lighting layers that shift from breakfast bright to dinner dim.",
      "Our own fitters handle the plumbing, electrics, tiling, plastering and joinery, so the whole project sits with one accountable team. Most kitchens are completed in three to four weeks with minimal disruption.",
    ],
    includes: [
      "Bespoke cabinetry & worktops",
      "Island design & open-plan flow",
      "Premium appliance installation",
      "Full rewire & lighting design",
      "Plumbing & waste reconfiguration",
      "Tiling, splashbacks & flooring",
      "Skylights & bi-fold openings",
      "Handover snagging & warranty",
    ],
  },
  {
    slug: "full-refurbishments",
    name: "Full Refurbishments",
    short: "Whole-home transformations — structure, systems and finish.",
    tagline: "Whole homes, completely transformed.",
    icon: PaintRoller,
    image: IMG.refurb,
    altImage: IMG.refurb2,
    description: [
      "A full refurbishment is the most demanding project in construction: electrics, plumbing, structure, layout and finish all changing at once. We run it like a small build — programmed, sequenced and project-managed by one dedicated manager who is on site every day.",
      "The result is a home that behaves like new: rewired, replumbed, re-insulated and reconfigured around the way you actually live, with every wall, floor and ceiling finished to a single, consistent standard.",
    ],
    includes: [
      "Whole-house structural reconfiguration",
      "Full rewire & smart-home readiness",
      "Replumbing & heating upgrades",
      "Energy-efficiency & insulation upgrades",
      "Plastering, floors & decoration",
      "Bespoke joinery & storage",
      "Kitchen & bathroom fit-outs",
      "Building-control sign-off & warranty",
    ],
  },
  {
    slug: "new-builds",
    name: "New Builds",
    short: "Custom homes built from the foundations up, turnkey.",
    tagline: "From foundations to front door.",
    icon: Building2,
    image: IMG.newBuilds,
    altImage: IMG.newBuilds2,
    description: [
      "Building your own home is the ultimate project — and the one where experience matters most. We take custom new builds from bare land to keys-in-hand: groundworks, structure, roof, systems and the full turnkey finish, all under one contract.",
      "You get a single point of contact, a fixed programme, and a home built to modern standards of insulation, airtightness and finish. We handle building control, inspections and certification at every stage.",
    ],
    includes: [
      "Groundworks & foundations",
      "Brickwork, blockwork & framing",
      "Roofing & weatherproofing",
      "Windows, doors & glazing",
      "First & second-fix electrics and plumbing",
      "Insulation & airtightness detailing",
      "Driveways, landscaping & external works",
      "Full turnkey interior finish",
    ],
  },
  {
    slug: "extensions",
    name: "Extensions",
    short: "Single & double-storey extensions with glass, steel and light.",
    tagline: "Room to grow, without moving.",
    icon: Blocks,
    image: IMG.extensions,
    altImage: IMG.extensions2,
    description: [
      "A well-designed extension does more than add floor area — it changes how your whole ground floor lives. Rear extensions, side returns and wrap-arounds that open the kitchen to the garden with bi-fold doors, skylights and flush thresholds.",
      "We handle design, party-wall matters, planning and building control, then build it with our own team. Most single-storey extensions are structurally complete in six to eight weeks.",
    ],
    includes: [
      "Single & double-storey extensions",
      "Rear, side-return & wrap-around designs",
      "Structural steel & open-plan knock-throughs",
      "Bi-fold, sliding & glazed doors",
      "Roof lanterns & skylights",
      "Underfloor heating & insulation",
      "Kitchen & utility fit-outs",
      "Planning & building-control management",
    ],
  },
];

export const PROCESS = [
  {
    num: "01",
    title: "Survey & Consultation",
    desc: "A free site survey and honest feasibility advice, followed by a detailed, itemised quotation — no hidden extras.",
  },
  {
    num: "02",
    title: "Design & Planning",
    desc: "Drawings, structural calculations, material samples and planning or building-control approvals, all handled for you.",
  },
  {
    num: "03",
    title: "Build & Construction",
    desc: "A directly employed, fully insured team on site daily — tidy sites, clear schedules and regular progress updates.",
  },
  {
    num: "04",
    title: "Handover & Warranty",
    desc: "A full snagging inspection, building-control certification and your 12-month workmanship warranty pack.",
  },
];

export const TRUST = [
  {
    value: "12-Month",
    label: "Workmanship Warranty",
    desc: "Comprehensive cover on every project, in writing.",
  },
  {
    value: "100%",
    label: "Fully Insured",
    desc: "Public liability & contractor all-risks cover.",
  },
  {
    value: "Turnkey",
    label: "All-Inclusive",
    desc: "Labour and premium materials in one clear quote.",
  },
  {
    value: "End-to-End",
    label: "Project Managed",
    desc: "One dedicated team from survey to handover.",
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "From the first survey to the final handover, everything was managed for us. Our loft conversion finished on time and the quality of finish is outstanding.",
    name: "Sarah M.",
    location: "Guildford",
    project: "Loft Conversion",
  },
  {
    quote:
      "The team turned a dated 90s kitchen into the heart of our home. Communication was excellent and the site was left spotless every single day.",
    name: "James & Emma T.",
    location: "Farnham",
    project: "Kitchen",
  },
  {
    quote:
      "Esskay handled planning, design and the build of our rear extension. Genuinely turnkey — we couldn't recommend them more highly.",
    name: "Priya K.",
    location: "Winchester",
    project: "Extension",
  },
];

export const GALLERY = [
  { img: IMG.living2, title: "Stone fireplace & oak floor", cat: "Full Refurbishments" },
  { img: IMG.bathrooms2, title: "Brushed-brass family bathroom", cat: "Bathrooms" },
  { img: IMG.kitchens2, title: "Marble-topped kitchen", cat: "Kitchens" },
  { img: IMG.refurb2, title: "Structural reconfiguration", cat: "Full Refurbishments" },
  { img: IMG.extensions2, title: "Rear extension, Surrey", cat: "Extensions" },
  { img: IMG.loft, title: "Velux loft bedroom", cat: "Loft Conversions" },
  { img: IMG.living1, title: "Open-plan living space", cat: "Full Refurbishments" },
  { img: IMG.kitchens3, title: "Shaker-style kitchen", cat: "Kitchens" },
  { img: IMG.bathrooms3, title: "Double-vanity en-suite", cat: "Bathrooms" },
  { img: IMG.craft2, title: "First-fix joinery", cat: "Full Refurbishments" },
  { img: IMG.newBuilds2, title: "New-build street scene", cat: "New Builds" },
  { img: IMG.brick, title: "Brick façade restoration", cat: "Extensions" },
];
